import React, { useEffect } from "react";
import { useDialog } from "../hooks/useDialog";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { isAbortedRequest, useRpcCaller } from "../context/WidgetRequestContext";
import { dietaryRequirementsToCustomerNotes, toDietaryRequirementsPayload } from "./preOrderUtils";
import { CUSTOMER_NOTES_MAX_LENGTH } from "../../../common/dietaryRequirements";
import "../../styles/dietary-modal.scss";
import PrimaryButton from "../buttons/primaryButton.tsx";
import { z } from "zod";
import { useInFlight } from "../hooks/useInFlight";
interface DietaryRequirementOut {
  name: string;
  diet_types: string[];
  allergies: string[];
  notes: string;
}

interface DietaryRequirement {
  diet_types: { [key: string]: boolean };
  allergies: { [key: string]: boolean };
  notes?: string;
}

const toDietaryRequirement = (name: string, req: DietaryRequirement, orderNotes: string): DietaryRequirementOut => ({
  name,
  diet_types: Object.keys(req.diet_types ?? {}).filter((key) => req.diet_types[key]),
  allergies: Object.keys(req.allergies ?? {}).filter((key) => req.allergies[key]),
  notes: orderNotes,
});

const arrayToBooleanRecord = (arr?: string[]): Record<string, boolean> => {
  const record: Record<string, boolean> = {};
  if (arr) {
    for (const item of arr) {
      record[String(item)] = true;
    }
  }
  return record;
};
const labelListSchema = z.array(z.string()).catch([]);

const sortedLabelOptions = (labels: ReturnType<TFunction>, language?: string): { name: string }[] => {
  const collator = new Intl.Collator(language, { sensitivity: "base" });
  return [...labelListSchema.parse(labels)].sort(collator.compare).map((name) => ({ name }));
};

// Extend the Window interface to include selectedAllergens
declare global {
  interface Window {
    selectedAllergens?: Record<string, boolean>;
    selectedDietTypes?: Record<string, boolean>;
  }
}

interface DietaryModalProps {
  show: boolean;
  onClose: () => void;
  inFlight?: ReturnType<typeof useInFlight>;
  forNames?: string[];
  customerPreOrder?: any;
  updateDietaryRequirements: (dietaryRequirements: any[]) => void;
}

// Move useTranslation inside the component to ensure i18n context is available
const DietaryModal: React.FC<DietaryModalProps> = ({
  show,
  onClose,
  forNames = [],
  customerPreOrder,
  updateDietaryRequirements,
  inFlight,
}) => {
  const call = useRpcCaller();
  const { t, i18n } = useTranslation();
  const localInFlight = useInFlight();
  const { isPending, run } = inFlight ?? localInFlight;
  const isMountedRef = React.useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Get diet types and allergens from translations for i18n support
  const language = i18n?.resolvedLanguage ?? i18n?.language;
  const DIET_TYPES = sortedLabelOptions(t("diet_types", { returnObjects: true }), language);
  const ALLERGENS = sortedLabelOptions(t("allergens", { returnObjects: true }), language);

  // Use array instead of object for requirements to keep order with forNames
  const [dietaryRequirementsByPerson, setDietaryRequirementsByPerson] = React.useState<DietaryRequirement[]>([]);
  // The "Order notes" field is one note for the whole pre-order, not per person —
  // it is mirrored onto every dietary requirement entry when saved.
  const [orderNotes, setOrderNotes] = React.useState("");
  const [currentPersonIndex, setCurrentPersonIndex] = React.useState(0);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  useEffect(() => {
    if (customerPreOrder?.dietaryRequirements) {
      const requirements = customerPreOrder.dietaryRequirements.map(
        (req: { diet_types?: string[]; allergies?: string[]; notes?: string }): DietaryRequirement => ({
          diet_types: arrayToBooleanRecord(req.diet_types),
          allergies: arrayToBooleanRecord(req.allergies),
          notes: req.notes ?? "",
        }),
      );
      setDietaryRequirementsByPerson(requirements);
      setOrderNotes(dietaryRequirementsToCustomerNotes(customerPreOrder.dietaryRequirements) ?? "");
    }
  }, [customerPreOrder]);

  const currentRequirements = dietaryRequirementsByPerson[currentPersonIndex] || {
    diet_types: {},
    allergies: {},
    notes: "",
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.selectedAllergens = currentRequirements.allergies;
      window.selectedDietTypes = currentRequirements.diet_types;
    }
  }, [currentRequirements]);

  const dialogRef = useDialog({ show, onClose });

  const handleConfirm = async (): Promise<void> => {
    setSaveError(null);
    if (customerPreOrder) {
      let dietaryRequirements: DietaryRequirementOut[];
      if (forNames.length === 0) {
        // Single person, no names added
        const req = dietaryRequirementsByPerson[0] ?? { diet_types: {}, allergies: {}, notes: "" };
        dietaryRequirements = [toDietaryRequirement("", req, orderNotes)];
      } else {
        // Multiple people — the order note is shared, so every entry carries it
        dietaryRequirements = forNames.map((name, idx) =>
          toDietaryRequirement(
            name,
            dietaryRequirementsByPerson[idx] ?? { diet_types: {}, allergies: {}, notes: "" },
            orderNotes,
          ),
        );
      }

      const validatedDietaryRequirements = toDietaryRequirementsPayload(dietaryRequirements);

      try {
        await call("/updateDietaryRequirements", {
          booking_id: customerPreOrder.booking._id,
          dietaryRequirements: validatedDietaryRequirements,
          ...(customerPreOrder.pending ? { pending: true } : {}),
        });

        updateDietaryRequirements(validatedDietaryRequirements);
      } catch (error) {
        if (isAbortedRequest(error)) return;
        console.error("Failed to persist dietary requirements:", error);
        if (isMountedRef.current) {
          setSaveError(
            t(
              "dietary_requirements_save_failed",
              "Sorry, we couldn't save your dietary requirements. Please try again.",
            ),
          );
        }
        return;
      }
    }
    if (isMountedRef.current) onClose();
  };

  if (!show) return null;

  return (
    <dialog
      id="gc-dietary-modal"
      ref={dialogRef}
      className="dietary-modal-dialog dietary-modal-backdrop"
      aria-modal="true"
    >
      <div className="dietary-modal-content" tabIndex={-1}>
        <div id="gc-dietary-modal-header" className="dietary-modal-header">
          <h3 className="payment-title gcw:mb-0">
            {t("add_dietary_requirements", "Dietary requirements")}
            {forNames.length > 0 && (
              <span>
                {" "}
                -{" "}
                {forNames[currentPersonIndex] ||
                  t("person_number", "Person {{number}}", { number: currentPersonIndex + 1 })}
              </span>
            )}
          </h3>
          <button type="button" className="dietary-modal-close" onClick={onClose} aria-label={t("close", "Close")}>
            ×
          </button>
        </div>
        <div id="gc-dietary-modal-body" className="dietary-modal-body">
          {forNames.length > 1 && (
            <div className="dietary-modal-person-picker">
              <p>{t("select_person", "Select person:")}</p>
              <div className="dietary-modal-person-picker-names">
                {forNames.map((name, index) => (
                  <button
                    key={name}
                    type="button"
                    disabled={isPending}
                    aria-busy={isPending || undefined}
                    className={`gcw:inline-flex gcw:items-center gcw:justify-center gcw:rounded-md gcw:border gcw:text-sm gcw:py-1 gcw:px-2 gcw:transition-colors dietary-multiple-names gcw:ms-1 ${
                      index === currentPersonIndex
                        ? "gcw:bg-primary gcw:text-white gcw:border-primary gcw:hover:bg-primary-hover"
                        : "gcw:bg-transparent gcw:text-gray-600 gcw:border-gray-300 gcw:hover:bg-gray-100"
                    }`}
                    onClick={() => setCurrentPersonIndex(index)}
                  >
                    {name}
                  </button>
                ))}
              </div>
              <hr className="dietary-modal-divider" />
            </div>
          )}

          <div className="dietary-modal-notes">
            <div className="dietary-modal-notes-header">
              <span className="dietary-modal-section-title">{t("order_notes", "Order notes")}</span>
            </div>
            <textarea
              className="dietary-modal-textarea"
              placeholder={t("enter_dietary_requirements", "Enter dietary requirements...")}
              disabled={isPending}
              aria-busy={isPending || undefined}
              aria-label={t("order_notes", "Order notes")}
              maxLength={CUSTOMER_NOTES_MAX_LENGTH}
              value={orderNotes}
              onChange={(e) => setOrderNotes(e.target.value)}
            />
          </div>
          <p className="dietary-modal-section-title">{t("select_diet_types", "Select any diet types that apply")}</p>
          <div className="dietary-modal-labels">
            {DIET_TYPES.map((type) => (
              <label className="dietary-modal-checkbox" key={type.name}>
                <input
                  type="checkbox"
                  disabled={isPending}
                  aria-busy={isPending || undefined}
                  checked={!!currentRequirements.diet_types[type.name]}
                  onChange={(e) => {
                    const updated = [...dietaryRequirementsByPerson];
                    updated[currentPersonIndex] = {
                      ...(updated[currentPersonIndex] || { diet_types: {}, allergies: {} }),
                      diet_types: {
                        ...(updated[currentPersonIndex]?.diet_types ?? {}),
                        [type.name]: e.target.checked,
                      },
                    };
                    setDietaryRequirementsByPerson(updated);
                  }}
                />
                {type.name}
              </label>
            ))}
          </div>
          <p className="dietary-modal-section-title">{t("select_allergies", "Select any allergies that apply")}</p>
          <div className="dietary-modal-labels">
            {ALLERGENS.map((type) => (
              <label className="dietary-modal-checkbox" key={type.name}>
                <input
                  type="checkbox"
                  disabled={isPending}
                  aria-busy={isPending || undefined}
                  checked={!!currentRequirements.allergies[type.name]}
                  onChange={(e) => {
                    const updated = [...dietaryRequirementsByPerson];
                    updated[currentPersonIndex] = {
                      ...(updated[currentPersonIndex] || { diet_types: {}, allergies: {} }),
                      allergies: {
                        ...(updated[currentPersonIndex]?.allergies ?? {}),
                        [type.name]: e.target.checked,
                      },
                    };
                    setDietaryRequirementsByPerson(updated);
                  }}
                />
                {type.name}
              </label>
            ))}
          </div>
        </div>
        {saveError && (
          <div
            className="gcw:rounded-md gcw:border gcw:border-error gcw:bg-error/10 gcw:text-error
              widget-error-panel gcw:p-2 gcw:text-sm gcw:mt-2"
            role="alert"
          >
            {saveError}
          </div>
        )}
        <div id="gc-dietary-modal-footer" className="dietary-modal-footer">
          <PrimaryButton
            text={t("confirm", "Confirm")}
            className="gcw:inline-flex gcw:items-center gcw:justify-center gcw:rounded-md gcw:bg-primary gcw:text-white gcw:border gcw:border-primary gcw:px-bs-3 gcw:py-2 gcw:font-medium gcw:transition-colors gcw:hover:bg-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 pay-button"
            disabled={isPending}
            loading={isPending}
            onClick={() => void run(handleConfirm)}
          />
        </div>
      </div>
    </dialog>
  );
};

export default DietaryModal;
